/**
 * Mock data for the Lawn Irrigation Dashboard.
 *
 * Used when the `/api/lawn/*` endpoints are unreachable, so the concept stays
 * demonstrable without a running backend. The shapes match `src/types/lawnTypes.js`
 * exactly, which is also what the API returns.
 */

/**
 * Placeholder yard art, drawn as an inline SVG so the prototype needs no binary
 * asset. Replaced as soon as the user picks their own photo.
 */
export const PLACEHOLDER_YARD_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 520" preserveAspectRatio="none">
      <defs>
        <linearGradient id="lawn" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#4b7f3f"/>
          <stop offset="100%" stop-color="#2f5d2a"/>
        </linearGradient>
      </defs>
      <rect width="800" height="520" fill="#2f5d2a"/>
      <rect width="800" height="520" fill="url(#lawn)"/>
      <path d="M0 360 Q 200 330 400 360 T 800 355 L800 520 L0 520 Z" fill="#26491f" opacity="0.75"/>
      <rect x="300" y="120" width="220" height="150" rx="8" fill="#7d6a58"/>
      <path d="M288 124 L410 60 L532 124 Z" fill="#5c4a3c"/>
      <rect x="352" y="196" width="46" height="74" rx="4" fill="#3c3128"/>
      <rect x="432" y="176" width="54" height="44" rx="4" fill="#9fb7c9"/>
      <path d="M96 300 q 24 -74 48 0 z" fill="#1f3d1b"/>
      <path d="M660 312 q 30 -88 60 0 z" fill="#1f3d1b"/>
      <path d="M40 430 Q 400 396 780 438" stroke="#8d9b7a" stroke-width="16" fill="none" opacity="0.5"/>
      <text x="400" y="502" font-family="system-ui, sans-serif" font-size="20" fill="#d8e4cd" text-anchor="middle" opacity="0.85">Placeholder yard — upload your own photo</text>
    </svg>`
  );

/** @type {import('../types/lawnTypes').Property} */
export const mockProperty = {
  id: 'prop_1',
  name: 'Home Lawn',
  address: '123 Example St, Prior Lake, MN',
  imageUrl: '',
  createdAt: '2026-05-01',
};

/** @type {import('../types/lawnTypes').Sensor[]} */
export const mockSensors = [
  {
    id: 'sen_1',
    propertyId: 'prop_1',
    name: 'Front yard',
    type: 'soil_moisture',
    xPct: 22,
    yPct: 40,
    batteryPct: 91,
    moisturePct: 34,
    status: 'online',
    lastReadingAt: '2026-09-20T08:00:00Z',
  },
  {
    id: 'sen_2',
    propertyId: 'prop_1',
    name: 'Back corner',
    type: 'soil_moisture',
    xPct: 68,
    yPct: 72,
    batteryPct: 12,
    moisturePct: 58,
    status: 'low_battery',
    lastReadingAt: '2026-09-19T08:00:00Z',
  },
  {
    id: 'sen_3',
    propertyId: 'prop_1',
    name: 'South strip',
    type: 'soil_moisture',
    xPct: 45,
    yPct: 24,
    batteryPct: 64,
    moisturePct: null,
    status: 'offline',
    lastReadingAt: '2026-09-14T08:00:00Z',
  },
];

/**
 * Five rolling days starting today, so the strip never looks stale in a demo.
 * At least one day recommends a rain skip.
 *
 * @returns {import('../types/lawnTypes').ForecastDay[]}
 */
export function buildMockForecast(propertyId = 'prop_1') {
  const shape = [
    { condition: 'sunny', highF: 74, lowF: 52, precipChancePct: 5, rainSkipRecommended: false },
    { condition: 'rain', highF: 66, lowF: 50, precipChancePct: 80, rainSkipRecommended: true },
    { condition: 'cloudy', highF: 69, lowF: 51, precipChancePct: 20, rainSkipRecommended: false },
    { condition: 'storms', highF: 71, lowF: 55, precipChancePct: 70, rainSkipRecommended: true },
    { condition: 'sunny', highF: 76, lowF: 54, precipChancePct: 10, rainSkipRecommended: false },
  ];

  const start = new Date();
  return shape.map((day, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return {
      id: `fc_${index + 1}`,
      propertyId,
      date: date.toISOString().slice(0, 10),
      ...day,
    };
  });
}

/** @type {import('../types/lawnTypes').Ordinance} */
export const mockOrdinance = {
  id: 'ord_1',
  propertyId: 'prop_1',
  municipality: 'Prior Lake',
  ruleType: 'odd_even',
  allowedDays: ['odd'],
  allowedTimeStart: '06:00',
  allowedTimeEnd: '10:00',
  notes: 'Odd-numbered addresses water on odd calendar days, before 10am or after 7pm.',
};

/** @type {import('../types/lawnTypes').DashboardWidget[]} */
export const mockWidgets = [
  { id: 'wid_1', widgetType: 'property_view', x: 0, y: 0, width: 7, height: 9, configJson: '{}' },
  { id: 'wid_2', widgetType: 'forecast', x: 7, y: 0, width: 5, height: 5, configJson: '{}' },
  { id: 'wid_3', widgetType: 'ordinance', x: 7, y: 5, width: 5, height: 4, configJson: '{}' },
  { id: 'wid_4', widgetType: 'sensor_detail', x: 0, y: 9, width: 5, height: 7, configJson: '{}' },
  { id: 'wid_5', widgetType: 'sensor_list', x: 5, y: 9, width: 7, height: 7, configJson: '{}' },
];

/**
 * Short moisture history for the sensor detail sparkline, derived so it always
 * ends at the sensor's current reading.
 *
 * @returns {import('../types/lawnTypes').SensorReading[]}
 */
export function buildMockReadings(moisturePct, count = 7) {
  if (moisturePct === null || moisturePct === undefined) {
    return [];
  }

  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const daysAgo = count - 1 - index;
    const drift = daysAgo * 3 - (index % 2) * 2;
    const date = new Date(now);
    date.setDate(now.getDate() - daysAgo);
    return {
      recordedAt: date.toISOString(),
      moisturePct: Math.max(0, Math.min(100, moisturePct + drift)),
    };
  });
}

/**
 * Full offline payload, matching `GET /api/lawn/dashboard/<username>`.
 *
 * @returns {import('../types/lawnTypes').LawnDashboardData}
 */
export function buildMockDashboard() {
  return {
    properties: [{ ...mockProperty }],
    property: { ...mockProperty },
    sensors: mockSensors.map((sensor) => ({ ...sensor })),
    forecast: buildMockForecast(mockProperty.id),
    ordinance: { ...mockOrdinance },
    widgets: mockWidgets.map((widget) => ({ ...widget })),
  };
}
